import Link from "next/link";
import { NoSymbolIcon } from "@heroicons/react/24/outline";

import ArrowLink from "@/components/ui/arrow-link";
import IconTile from "@/components/ui/icon-tile";
import { getModuleHue } from "@/lib/modules/module-hue";
import { getTone } from "@/lib/ui/tones";
import { cn } from "@/lib/utils";

/**
 * The one card that opens a practice module, in two sizes: `compact` is the
 * one-line card in the home page's "Jump into a session" row, `card` is the
 * tile in the Practice grid (design-guidelines §5). Both are the same bordered
 * link: the module's icon tile in the module's own colour (`getModuleHue`, the
 * hue its tags and result headers wear), its title, and the blue arrow
 * (`ArrowLink`). The card adds the description and spells out "Start →"; the
 * compact size has room for the arrow alone.
 *
 * The whole surface is the link, so the arrow is a label, not a control (§7):
 * a span, never a `<button>` or a second `<a>` inside the `<a>`. Hover changes
 * colour only (the border takes the module's hue, the arrow nudges); the one
 * motion is a press. A `card` with `disabled` renders muted and unlinked, with
 * `disabledReason` where "Start →" would be.
 *
 * Not for: a module tab that selects rather than opens (RubricModuleTabs),
 * or the persona page's card-less module rows.
 *
 * No entry in primitive-shapes.mjs: a bordered link with an icon is every
 * card link. The guard is `/same-action` (`--key list:module` shows one look).
 */
const SIZES = {
  compact: "flex items-center gap-3 rounded-xl p-3",
  card: "flex h-full flex-col gap-3 rounded-xl p-5",
};

// The press is the shared `press-dip` (app/globals.css), shallower for a card.
const BASE = "group press-dip border [--press-scale:0.985]";
const LIVE =
  "border-gray-200 bg-white text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2";
const DISABLED = "border-gray-100 bg-gray-50 opacity-70";
const NEUTRAL_HOVER = "hover:border-gray-300";

function CompactContent({ title, icon, hue }) {
  return (
    <>
      <IconTile icon={icon} size="sm" hue={hue} />
      <span className="min-w-0 flex-1 line-clamp-2 text-sm font-semibold leading-snug text-gray-900">
        {title}
      </span>
      <ArrowLink />
    </>
  );
}

function CardContent({ title, description, icon, hue, disabled, disabledReason }) {
  return (
    <>
      <IconTile icon={icon} size="lg" hue={hue} />
      <h3 className="text-base font-semibold leading-snug tracking-tight text-gray-900">
        {title}
      </h3>
      <p className="line-clamp-3 min-h-[3lh] text-sm leading-relaxed text-gray-600">
        {description}
      </p>
      <div className="mt-auto flex items-center justify-end pt-1">
        {disabled ? (
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <NoSymbolIcon className="h-4 w-4" />
            {disabledReason}
          </span>
        ) : (
          <ArrowLink>Start</ArrowLink>
        )}
      </div>
    </>
  );
}

export default function ModuleLaunch({
  href,
  title,
  description,
  icon,
  size = "card",
  disabled = false,
  disabledReason,
}) {
  const compact = size === "compact";
  const layout = compact ? SIZES.compact : SIZES.card;
  const hue = getModuleHue(href);
  const content = compact ? (
    <CompactContent title={title} icon={icon} hue={hue} />
  ) : (
    <CardContent
      title={title}
      description={description}
      icon={icon}
      hue={hue}
      disabled={disabled}
      disabledReason={disabledReason}
    />
  );

  if (disabled) {
    return <div className={cn(BASE, DISABLED, layout)}>{content}</div>;
  }
  return (
    <Link
      href={href}
      className={cn(BASE, LIVE, (hue && getTone(hue).hoverBorder) || NEUTRAL_HOVER, layout)}
    >
      {content}
    </Link>
  );
}
